import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Spin, message } from 'antd';
import { UserOutlined, ShoppingCartOutlined, DollarOutlined, ApiOutlined, TeamOutlined } from '@ant-design/icons';
import { Line } from '@ant-design/charts';
import { statsAPI } from '../services/api';

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalOrders: 0,
    totalRevenue: 0,
    totalCalls: 0,
    totalAffiliates: 0,
    callTrend: [],
    revenueTrend: [],
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const response = await statsAPI.getDashboard();
      if (response.code === 0) {
        // 兼容 snake_case 和 camelCase
        const data = response.data || {};
        setStats({
          totalUsers: data.totalUsers ?? data.total_users ?? 0,
          totalOrders: data.totalOrders ?? data.total_orders ?? 0,
          totalRevenue: data.totalRevenue ?? data.total_revenue ?? 0,
          totalCalls: data.totalCalls ?? data.total_calls ?? 0,
          totalAffiliates: data.totalAffiliates ?? data.total_affiliates ?? 0,
          callTrend: data.callTrend ?? data.call_trend ?? [],
          revenueTrend: data.revenueTrend ?? data.revenue_trend ?? [],
        });
      }
    } catch (error) {
      message.error('获取统计数据失败');
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: '总用户数',
      value: stats.totalUsers ?? 0,
      icon: <UserOutlined style={{ fontSize: 32, color: '#1890ff' }} />,
      color: '#1890ff',
    },
    {
      title: '商户数',
      value: stats.totalAffiliates ?? 0,
      icon: <TeamOutlined style={{ fontSize: 32, color: '#eb2f96' }} />,
      color: '#eb2f96',
    },
    {
      title: '总订单数',
      value: stats.totalOrders ?? 0,
      icon: <ShoppingCartOutlined style={{ fontSize: 32, color: '#52c41a' }} />,
      color: '#52c41a',
    },
    {
      title: '总收入（美元）',
      value: `$${(stats.totalRevenue ?? 0).toFixed(2)}`,
      icon: <DollarOutlined style={{ fontSize: 32, color: '#faad14' }} />,
      color: '#faad14',
    },
    {
      title: '总调用量',
      value: stats.totalCalls ?? 0,
      icon: <ApiOutlined style={{ fontSize: 32, color: '#722ed1' }} />,
      color: '#722ed1',
    },
  ];

  const callChartConfig = {
    data: stats.callTrend,
    xField: 'date',
    yField: 'count',
    point: {
      size: 4,
      shape: 'circle',
    },
    label: {
      style: {
        fill: '#aaa',
      },
    },
    smooth: true,
  };

  const revenueChartConfig = {
    data: stats.revenueTrend,
    xField: 'date',
    yField: 'amount',
    point: {
      size: 4,
      shape: 'circle',
    },
    label: {
      style: {
        fill: '#aaa',
      },
    },
    smooth: true,
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="fit-screen-page">
      {/* 功能区 */}
      <div className="page-header">
        <h2 className="page-title">仪表板</h2>
      </div>
      
      {/* 统计卡片 */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', flexShrink: 0, marginBottom: 16 }}>
        {statCards.map((card, index) => (
          <Card key={index} className="stat-card" style={{ flex: '1 1 180px', minWidth: 180 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div className="stat-value" style={{ color: card.color }}>{card.value}</div>
                <div className="stat-title">{card.title}</div>
              </div>
              {card.icon}
            </div>
          </Card>
        ))}
      </div>

      {/* 趋势图表 */}
      <div style={{ flex: 1, minHeight: 0 }}>
        <Row gutter={[16, 16]} style={{ height: '100%' }}>
          <Col xs={24} lg={12} style={{ height: '100%' }}>
            <Card title="调用趋势（最近7天）" style={{ height: '100%' }} styles={{ body: { height: 'calc(100% - 57px)', minHeight: 200 } }}>
              {stats.callTrend.length > 0 ? (
                <Line {...callChartConfig} style={{ height: '100%' }} />
              ) : (
                <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                  暂无数据
                </div>
              )}
            </Card>
          </Col>
          <Col xs={24} lg={12} style={{ height: '100%' }}>
            <Card title="收入趋势（最近7天）" style={{ height: '100%' }} styles={{ body: { height: 'calc(100% - 57px)', minHeight: 200 } }}>
              {stats.revenueTrend.length > 0 ? (
                <Line {...revenueChartConfig} style={{ height: '100%' }} />
              ) : (
                <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                  暂无数据
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default Dashboard;
